import styled from 'styled-components'
import { useRouter } from 'next/router'
import Button from '@/components/ui/Button'
import { HOME_PAGE } from '@/common/constants/analytics'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { device } from '@/common/constants/device'
import { useTranslations } from 'next-intl'

const Wrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${device.laptop} {
    margin: 50px 0;
  }
`

const SubTitle = styled.h2`
  font-weight: 400;
  font-size: 36px;
  line-height: 46px;
  color: #0d0d12;
  text-align: center;
`

const Title = styled.h1`
  font-weight: 800;
  font-size: 48px;
  line-height: 54px;
  margin-top: 8px;
  color: #0d0d12;
  text-align: center;
`

const SubText = styled.p`
  margin: 16px auto 0;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(63, 63, 73, 1);
  max-width: 600px;
  text-align: center;
`

const Buttons = styled.div`
  margin-top: 32px;
`

const Btn = styled(Button)`
  width: fit-content;
  height: 50px;
  min-width: 224px;
`

const RatingSection = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`

const Avatars = styled.div`
  display: flex;
  margin-right: 16px;

  img {
    width: 223px;
    height: 34px;
  }
`

const Stars = styled.div`
  font-size: 20px;
  color: #f6bb55;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const RatingText = styled.div`
  color: rgba(63, 63, 73, 1);
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  text-align: center;
`

const CardsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  margin-top: 80px;
  max-width: 1280px;
`

const Card = styled.div`
  flex: 1 1 250px;
  max-width: 288px;
  background: white;
  border-radius: 24px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const CardTitle = styled.h3`
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  margin-top: 20px;
`

const CardText = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 160%;
  color: rgba(122, 122, 122, 1);
  margin: 16px 0;
`

const CardBtn = styled(Button)`
  height: 40px;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  width: 100%;
  margin-top: auto;
  transition: all 0.26s ease-in-out;

  &.red {
    border: 1px solid #ec011a;
    color: #ec011a;

    &:hover {
      background-color: #ec011a;
      color: white;
    }
  }

  &.blue {
    border: 1px solid #0866ff;
    color: #0866ff;

    &:hover {
      background-color: #0866ff;
      color: white;
    }
  }

  &.black {
    border: 1px solid #000000;
    color: #000000;

    &:hover {
      background-color: #000000;
      color: white;
    }
  }
`

const translations = {
  var1_control: {
    title: 'landingVar1Title',
    subTitle: 'landingVar1SubTitle',
    cta: 'landingVar1Cta',
    subtext: 'landingVar1SubText',
  },
  var2: {
    title: 'landingVar2Title',
    subTitle: 'landingVar2SubTitle',
    cta: 'landingVar2Cta',
    subtext: 'landingVar2SubText',
  },
  var3: {
    title: 'landingVar3Title',
    subTitle: 'landingVar3SubTitle',
    cta: 'landingVar3Cta',
    subtext: 'landingVar3SubText',
  },
  var4: {
    title: 'landingVar4Title',
    subTitle: 'landingVar4SubTitle',
    cta: 'landingVar4Cta',
    subtext: 'landingVar4SubText',
  },
  var5: {
    title: 'landingVar5Title',
    subTitle: 'landingVar5SubTitle',
    cta: 'landingVar5Cta',
    subtext: 'landingVar5SubText',
  },
  var6: {
    title: 'landingVar6Title',
    subTitle: 'landingVar6SubTitle',
    cta: 'landingVar6Cta',
    subtext: 'landingVar6SubText',
  },
  var7: {
    title: 'landingVar7Title',
    subTitle: 'landingVar7SubTitle',
    cta: 'landingVar7Cta',
    subtext: 'landingVar7SubText',
  },
  var8: {
    title: 'landingVar8Title',
    subTitle: 'landingVar8SubTitle',
    cta: 'landingVar8Cta',
    subtext: 'landingVar8SubText',
  },
  var9: {
    title: 'landingVar9Title',
    subTitle: 'landingVar9SubTitle',
    cta: 'landingVar9Cta',
    subtext: 'landingVar9SubText',
  },
}

const Landing6 = ({ experimentKey }) => {
  const router = useRouter()
  const t = useTranslations('index')
  const onGetStartClick = () => {
    userAmplitudeAnalytics.trackEvent(`${HOME_PAGE}_page_hero_cta_clicked`)
    router.push('/register')
  }
  
  const key = translations[experimentKey]

  return (
    <Wrapper>
      <SubTitle>{t(key.subTitle)}</SubTitle>
      <Title>{t(key.title)}</Title>
      <SubText>{t(key.subtext)}</SubText>
      <Buttons>
        <Btn filled onClick={onGetStartClick}>
          {t(key.cta)}
        </Btn>
      </Buttons>

      <RatingSection>
        <Avatars>
          <img src={`/_next_static/landing/icons.png`} alt={`user`} />
        </Avatars>
        <Container>
          <Stars>★★★★★</Stars>
          <RatingText>{t('landing_hero_rating_text')}</RatingText>
        </Container>
      </RatingSection>

      <CardsGrid>
        <Card>
          <img
            src="/_next_static/landing/facebook.svg"
            alt="facebook"
            width="38px"
            height="38px"
          />
          <CardTitle>{t('landing_hero_card_facebook_title')}</CardTitle>
          <CardText>{t('landing_hero_card_facebook_text')}</CardText>
          <CardBtn className="blue" onClick={onGetStartClick}>
            {t('landing_hero_get_started')}
          </CardBtn>
        </Card>

        <Card>
          <img
            src="/_next_static/landing/instagram.svg"
            alt="instagram"
            width="38px"
            height="38px"
          />
          <CardTitle>{t('landing_hero_card_instagram_title')}</CardTitle>
          <CardText>{t('landing_hero_card_instagram_text')}</CardText>
          <CardBtn className="red" onClick={onGetStartClick}>
            {t('landing_hero_get_started')}
          </CardBtn>
        </Card>

        <Card>
          <img
            src="/_next_static/landing/package.svg"
            alt="mobile apps"
            width="38px"
            height="38px"
          />
          <CardTitle>{t('landing_hero_card_apps_title')}</CardTitle>
          <CardText>{t('landing_hero_card_apps_text')}</CardText>
          <CardBtn className="blue" onClick={onGetStartClick}>
            {t('landing_hero_get_started')}
          </CardBtn>
        </Card>

        <Card>
          <img
            src="/_next_static/landing/camera-lens.svg"
            alt="AI tools"
            width="38px"
            height="38px"
          />
          <CardTitle>{t('landing_hero_card_ai_title')}</CardTitle>
          <CardText>{t('landing_hero_card_ai_text')}</CardText>
          <CardBtn className="black" onClick={onGetStartClick}>
            {t('landing_hero_get_started')}
          </CardBtn>
        </Card>
      </CardsGrid>
    </Wrapper>
  )
}

export default Landing6
